import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchUkrposhtaTracking, type UkrposhtaTrackingStatus } from '@/services/orderService';
import { logging } from '@/services/logging';

const UKRPOSHTA_TRACKING_URL = 'https://track.ukrposhta.ua/tracking_UA.html';

interface UkrposhtaTrackingDialogProps {
  readonly open: boolean;
  readonly orderId: string | null;
  readonly onClose: () => void;
}

const resolveTrackingLang = (locale: string): 'UA' | 'EN' => {
  const normalized = locale.trim().toLowerCase();
  return normalized.startsWith('uk') ? 'UA' : 'EN';
};

export const UkrposhtaTrackingDialog = ({
  open,
  orderId,
  onClose,
}: UkrposhtaTrackingDialogProps) => {
  const { t, i18n } = useTranslation();
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
  const [trackingStatuses, setTrackingStatuses] = useState<ReadonlyArray<UkrposhtaTrackingStatus>>([]);
  const [trackingAvailability, setTrackingAvailability] = useState<'AVAILABLE' | 'NOT_FOUND' | 'UNAVAILABLE'>('AVAILABLE');
  const [trackingStatus, setTrackingStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const trackingLang = useMemo(() => resolveTrackingLang(i18n.language || navigator.language), [i18n.language]);

  useEffect(() => {
    if (!open || !orderId) {
      return;
    }

    let cancelled = false;
    setTrackingNumber(null);
    setTrackingStatuses([]);
    setTrackingAvailability('AVAILABLE');
    setTrackingStatus('loading');
    setTrackingError(null);
    setCopyState('idle');

    void fetchUkrposhtaTracking(orderId, trackingLang)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setTrackingNumber(payload.trackingNumber);
        setTrackingAvailability(payload.availability);
        setTrackingStatuses(payload.statuses);
        setTrackingStatus('idle');
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        logging.error('orders.fetch_tracking_failed', { orderId, error });
        setTrackingError(error instanceof Error ? error.message : 'Unable to load tracking details.');
        setTrackingStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [open, orderId, trackingLang]);

  useEffect(() => {
    if (copyState !== 'copied') {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setCopyState('idle');
    }, 1800);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [copyState]);

  const locale = i18n.language || navigator.language;
  const formatDate = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return (value: string) => formatter.format(new Date(value));
  }, [locale]);

  const handleCopyTrackingNumber = async () => {
    if (!trackingNumber) {
      return;
    }

    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopyState('copied');
    } catch (error) {
      logging.error('orders.copy_tracking_number_failed', { orderId, error });
      setCopyState('error');
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[85vh] w-[90vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-card p-4 text-card-foreground shadow-lg focus:outline-none">
          <div className="space-y-4">
            <div className="space-y-1">
              <Dialog.Title className="text-lg font-semibold">
                {t('orders.dialog.shipmentTrackingTitle', 'Ukrposhta tracking')}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground">
                {t('orders.dialog.shipmentTrackingDescription', 'Live Ukrposhta shipment events for your order.')}
              </Dialog.Description>
            </div>
            {trackingStatus === 'loading' ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('orders.dialog.trackingLoading', 'Loading tracking details...')}
              </div>
            ) : null}
            {trackingStatus === 'error' ? (
              <div className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {trackingError ?? t('orders.dialog.trackingError', 'Unable to load tracking details.')}
              </div>
            ) : null}
            {trackingStatus === 'idle' ? (
              trackingAvailability === 'NOT_FOUND' ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  {t(
                    'orders.dialog.trackingNotFound',
                    'Tracking details are not available yet for this shipment.',
                  )}
                </div>
              ) : trackingAvailability === 'UNAVAILABLE' ? (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  {t(
                    'orders.dialog.trackingUnavailable',
                    'Tracking is temporarily unavailable. Please try again later.',
                  )}
                </div>
              ) : trackingStatuses.length > 0 ? (
                <div className="space-y-3">
                  {trackingStatuses.map((entry, index) => (
                    <div key={`${entry.barcode}-${entry.step ?? 'step'}-${entry.date ?? index}`} className="rounded-md border p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="font-medium">
                          {entry.eventName ?? t('orders.dialog.trackingUnknownEvent', 'Status update')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {entry.date ? formatDate(entry.date) : '—'}
                        </div>
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                        <div>{[entry.index, entry.name, entry.country].filter(Boolean).join(' · ') || '—'}</div>
                        {entry.eventReason ? <div>{entry.eventReason}</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                  {t('orders.dialog.trackingEmpty', 'No tracking events are available yet.')}
                </div>
              )
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={UKRPOSHTA_TRACKING_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm font-medium text-primary underline underline-offset-2"
                >
                  {t('orders.dialog.openOfficialTracking', 'Open official tracking page')}
                </a>
                {trackingNumber ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {t('orders.dialog.trackingNumberShortLabel', 'Track No')}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        void handleCopyTrackingNumber();
                      }}
                      className="rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm font-medium text-foreground transition-colors hover:bg-muted"
                    >
                      {trackingNumber}
                    </button>
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${
                        copyState === 'copied'
                          ? 'bg-emerald-100 text-emerald-700'
                          : copyState === 'error'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {copyState === 'copied'
                        ? t('orders.dialog.trackingNumberCopied', 'Copied')
                        : copyState === 'error'
                          ? t('orders.dialog.trackingNumberCopyError', 'Copy failed')
                          : t('orders.dialog.trackingNumberHint', 'Click to copy')}
                    </span>
                  </div>
                ) : null}
              </div>
              <Dialog.Close asChild>
                <Button variant="default">{t('common.close', 'Close')}</Button>
              </Dialog.Close>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
